import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { allignColumnTreeview } from "../../modelViews/GraficElementUtils";
import { sortArrayByProperty } from "../../models/utils";
import BaseModel from "../../models/BaseModel";
import ColumnFilter from "../../models/ColumnFilter";
import WatchableValue from "../../models/WatchableValue";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { useRedraw } from "../../models/useRerender";
import type { ShowNameMode } from "../ObjectReference";
import UniversalObjectTableHeaderRow from "./UniversalObjectTableHeaderRow";
import UniversalObjectTableRows from "./UniversalObjectTableRows";
import BaseObject from "../../models/BaseObject";

/** Darstellung einer Spalte (analog zu ObjectReference). Enthält optional den Spaltenfilter. */
export class ColumnConfig extends BaseObject {
  get className() {
    return "ColumnConfig"; // no itx
  }

  showTag: boolean = true;
  showFullTag: boolean = false;
  showName: ShowNameMode | boolean = "onHover"; // no itx
  showIcon: boolean = true;
  title: string = "";
  /** Spaltenfilter (wird von UniversalObjectTable gesetzt, wenn nicht übergeben). */
  private _filter: ColumnFilter = new ColumnFilter();

  get filter(): ColumnFilter {
    return this._filter;
  }

  set filter(value: ColumnFilter) {
    this._filter = value;
  }

  columnType: "object" | "parent" | "grandparent" | undefined = undefined;

  getObject?: (obj: BaseModel) => BaseModel | undefined;
  /** Custom filter function. If set, used instead of the default tag/name filter. */
  filterFunc?: (obj: BaseModel, filterExpression: string) => boolean;

  constructor(init?: Partial<ColumnConfig>) {
    super();
    if (init) {
      if (init.showTag !== undefined) {
        this.showTag = init.showTag;
      }
      if (init.showName !== undefined) {
        this.showName = init.showName;
      }
      if (init.showIcon !== undefined) {
        this.showIcon = init.showIcon;
      }
      if (init.title !== undefined) {
        this.title = init.title;
      }
      if (init.filter !== undefined) {
        this.filter = init.filter;
      }
      if (init.columnType !== undefined) {
        this.columnType = init.columnType;
      }
      if (init.getObject !== undefined) {
        this.getObject = init.getObject;
      }
      if (init.showFullTag !== undefined) {
        this.showFullTag = init.showFullTag;
      }
      if (init.filterFunc !== undefined) {
        this.filterFunc = init.filterFunc;
      }
    }
  }
}

type ColumnType = ColumnConfig["columnType"];

export type AdditionalColumnConfig = {
  columnConfig: ColumnConfig;
  position: "left" | "right"; // no itx
  objProperty?: string;
  element?: (obj: BaseModel) => JSX.Element;
  /** Custom filter function for this column. Called when filterExpression is set. */
  filterFunc?: (obj: BaseModel, filterExpression: string) => boolean;
};

export type UniversalObjectTableProps = {
  title: string;
  footer?: React.ReactNode[];

  objects: WatchableValue<BaseModel[]>;
  id?: string;
  columnClassNames?: string[];
  maxColumnWidth?: number;
  onUnlink?: (obj: BaseModel | undefined) => void;
  /** Wenn gesetzt: Checkbox pro Zeile; checked wenn Objekt in diesem Array. Klick toggelt Mitgliedschaft. */
  selectedObjects?: WatchableValue<Set<BaseModel>>;
  onSelectionChange?: () => void;

  /** Titel-Bereich anzeigen (z. B. false wenn in ConnectObjectsPanel eingebettet). Default: true */
  showTitle?: boolean;
  /** Objekt-Spalte: Darstellung gemäß ColumnConfig. Wenn undefined: Defaults (Tag, Icon, Name je nach Filter). */
  objectColumnConfig: ColumnConfig;
  /** Parent-Spalte: wenn undefined, wird sie nicht angezeigt. Anzeige und Tag-Filter verwenden den Fulltag. */
  parentColumnConfig?: ColumnConfig;
  /** Großeltern-Spalte: wenn undefined, wird sie nicht angezeigt. Sonst Darstellung gemäß ColumnConfig. */
  grandparentColumnConfig?: ColumnConfig;
  /** Breite des Containers (z. B. "100%" wenn eingebettet). Default: "50%" */
  width?: string;

  additionalColumns?: AdditionalColumnConfig[];

  sort?: {
    func?: (objects: BaseModel[]) => BaseModel[];
    propertyName?: string;
  };
};

export default function UniversalObjectTable(props: UniversalObjectTableProps) {
  const {
    title,
    footer,
    objects,
    id = "UniversalObjectTable",
    maxColumnWidth = 600,
    onUnlink,
    selectedObjects,
    onSelectionChange,
    showTitle = true,
    objectColumnConfig,
    parentColumnConfig,
    grandparentColumnConfig,
    width = "50%",
    additionalColumns,
    sort,
  } = props;

  const showParent = parentColumnConfig !== undefined;
  const showGrandparent = grandparentColumnConfig !== undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const alignColumnsTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const redraw = useRedraw();

  const objectsList = objects.value;

  const grandparentColumnFilter = grandparentColumnConfig?.filter;
  const parentColumnFilter = parentColumnConfig?.filter;
  const objectColumnFilter = objectColumnConfig?.filter!;

  const filteredAndSortedObjects = useRef(new WatchableValue<BaseModel[]>([]));

  useEffect(() => {
    enhanceColumnDefinitions();
    filterObjects();
  }, []);

  useOnChangeWatcher(objects, () => {
    filterObjects();
    return false;
  });

  function onFilterChange(columnConfig: ColumnConfig) {
    if (columnConfig == grandparentColumnConfig) {
      if (parentColumnConfig) {
        parentColumnConfig.filter.filterExpression = "";
        parentColumnConfig.filter.raiseOnChange("filterExpression");
      }
      if (objectColumnConfig) {
        objectColumnConfig.filter.filterExpression = "";
        objectColumnConfig.filter.raiseOnChange("filterExpression");
      }
    } else if (columnConfig == parentColumnConfig) {
      if (objectColumnConfig) {
        objectColumnConfig.filter.filterExpression = "";
        objectColumnConfig.filter.raiseOnChange("filterExpression");
      }
    } else if (columnConfig == objectColumnConfig) {
    }

    filterObjects();
    updateSelectedObjects();
  }

  const columnClassNames = props.columnClassNames ?? [
    ...(showGrandparent ? ["col-grandparent"] : []), // no itx
    ...(showParent ? ["col-parent"] : []), // no itx
    "col-object", // no itx
  ];

  function enhanceColumnDefinitions() {
    if (grandparentColumnConfig) {
      grandparentColumnConfig.columnType = "grandparent";
      grandparentColumnConfig.filter.isFullTagFilter = true;
    }

    if (parentColumnConfig) {
      parentColumnConfig.columnType = "parent";
      parentColumnConfig.filter.isFullTagFilter = true;
    }

    if (objectColumnConfig) {
      objectColumnConfig.columnType = "object";
      objectColumnConfig.filter.isFullTagFilter = !parentColumnConfig;
    }

    for (const columnConfig of [grandparentColumnConfig, parentColumnConfig, objectColumnConfig]) {
      if (columnConfig?.columnType !== undefined) {
        columnConfig.filter.options = undefined;
        columnConfig.filter.getOptions = () => {
          return getFilterOptionsList(columnConfig);
        };
        columnConfig.filter.raiseOnChange();
      }
    }

    if (additionalColumns) {
      for (let i = 0; i < additionalColumns.length; i++) {
        const additionalColumn = additionalColumns[i];
        const columnConfig = additionalColumn.columnConfig;

        if (columnConfig?.columnType == undefined && !columnConfig.getObject) {
          columnConfig.filter.active = false;
          //          (columnConfig.filter as any).mweTest = `mweTest-${i}`;
        } else {
          columnConfig.filter.options = undefined;
          columnConfig.filter.getOptions = () => {
            return getFilterOptionsList(columnConfig);
          };
        }

        console.log("additionalColumn", i, columnConfig.filter.active);

        columnConfig.showFullTag = true;

        if (additionalColumn.position == "right" && i == additionalColumns.length - 1) {
          columnConfig.showName = "always"; // no itx
        }
        columnConfig.filter.raiseOnChange();
      }
    }
  }

  function getFilterOptionsList(columnConfig: ColumnConfig): string[] {
    const columnType = columnConfig.columnType;

    const list = objects.value;
    const res = new Set<string>();

    list.forEach((obj) => {
      if (columnConfig.getObject) {
        const object = columnConfig.getObject(obj);
        if (object) {
          if (columnConfig.showFullTag) {
            res.add(object.fullTag);
          } else {
            res.add(object.tag);
          }
        }
      } else {
        switch (columnType) {
          case "grandparent":
            if (obj.parent?.parent) {
              res.add(obj.parent?.parent?.fullTag);
            }
            break;

          case "parent":
            if (obj.parent) {
              res.add(obj.parent?.fullTag);
            }
            break;

          case "object":
            res.add(obj.tag);
            break;
        }
      }
    });

    return [...res].sort();
  }

  function filterObjects() {
    const list = objects.value;
    let fObjs: BaseModel[] = list;

    if (grandparentColumnFilter?.filterExpression || parentColumnFilter?.filterExpression) {
      fObjs = list.filter((obj) => {
        let res = true;
        if (grandparentColumnFilter?.filterExpression && obj.parent?.parent) {
          res = grandparentColumnFilter.evaluate(obj.parent.parent as BaseModel);
        }
        if (res && parentColumnFilter?.filterExpression && obj.parent) {
          res = parentColumnFilter.evaluate(obj.parent as BaseModel);
        }
        return res;
      });
    }
    if (objectColumnFilter.filterExpression) {
      const expr = objectColumnFilter.filterExpression;
      if (objectColumnConfig.filterFunc) {
        fObjs = fObjs.filter((obj) => objectColumnConfig.filterFunc!(obj, expr));
      } else {
        fObjs = fObjs.filter((obj) => objectColumnConfig.filter.evaluate(obj));
      }
    }

    if (additionalColumns) {
      for (const addCol of additionalColumns) {
        const expr = addCol.columnConfig.filter.filterExpression;
        if (expr && addCol.filterFunc) {
          fObjs = fObjs.filter((obj) => addCol.filterFunc!(obj, expr));
        }
      }
    }

    function getSortedObjects(input: Array<BaseModel>): Array<BaseModel> {
      if (sort?.func) return sort.func(input);
      if (sort?.propertyName) return sortArrayByProperty<BaseModel>(input, sort.propertyName);
      return sortArrayByProperty<BaseModel>(input, "simpleFullTag");
    }

    filteredAndSortedObjects.current.value = getSortedObjects(fObjs);
  }

  function updateSelectedObjects() {
    const objects = filteredAndSortedObjects.current.value;

    if (selectedObjects) {
      const set = selectedObjects.value;
      [...set].forEach((obj) => {
        if (!objects.includes(obj)) {
          set.delete(obj);
        }
      });

      objects.forEach((obj) => {
        set.add(obj);
      });

      selectedObjects.value = new Set(set);
      onSelectionChange?.();
    }
  }

  function allignColumns() {
    if (alignColumnsTimer.current) {
      clearTimeout(alignColumnsTimer.current);
    }

    alignColumnsTimer.current = setTimeout(() => {
      alignColumnsTimer.current = undefined;
      if (containerRef.current && columnClassNames?.length) {
        columnClassNames.forEach((className) => {
          allignColumnTreeview(className, containerRef.current as HTMLElement, maxColumnWidth);
        });
        additionalColumns?.forEach((column, index) => {
          allignColumnTreeview(`acol-${index}`, containerRef.current as HTMLElement, maxColumnWidth);
        });
      }
    }, 100);
  }

  function handleSelectAll(e: React.ChangeEvent<HTMLInputElement>) {
    if (!selectedObjects) return;
    const checked = e.target.checked;
    const set = selectedObjects.value;

    const objects = filteredAndSortedObjects.current.value;
    objects.forEach((obj) => {
      if (checked) set.add(obj);
      else set.delete(obj);
    });

    selectedObjects.value = new Set(set);
    onSelectionChange?.();
  }

  const showHeader = true;

  function TableBody() {
    useEffect(() => {
      allignColumns();
    });

    useOnChangeWatcher(filteredAndSortedObjects.current, () => {
      allignColumns();
      return false;
    });

    return (
      <div className="estadaColumn w100 h100 left top" style={{ overflow: "hidden" }}>
        {showHeader && (
          <UniversalObjectTableHeaderRow
            id={id}
            selectedObjects={selectedObjects}
            objects={filteredAndSortedObjects.current}
            onSelectAll={handleSelectAll}
            onFilterChange={(columnConfig) => onFilterChange(columnConfig)}
            grandparentColumnConfig={grandparentColumnConfig}
            parentColumnConfig={parentColumnConfig}
            objectColumnConfig={objectColumnConfig}
            additionalColumns={additionalColumns}
          />
        )}
        <UniversalObjectTableRows
          objects={filteredAndSortedObjects.current}
          selectedObjects={selectedObjects}
          onSelectionChange={onSelectionChange}
          additionalColumns={additionalColumns}
          grandparentColumnConfig={grandparentColumnConfig}
          parentColumnConfig={parentColumnConfig}
          objectColumnConfig={objectColumnConfig}
          onUnlink={onUnlink}
        />
      </div>
    );
  }

  useEffect(() => {
    return () => {
      if (alignColumnsTimer.current) {
        clearTimeout(alignColumnsTimer.current);
        alignColumnsTimer.current = undefined;
      }
    };
  }, []);
  return (
    <div
      id={id}
      ref={containerRef}
      className="universalObjectTable estadaColumn left top h100"
      style={{
        width,
        boxSizing: "border-box",
      }}
    >
      {showTitle && <div className="header1 estadaRow left w100">{title}</div>}

      <TableBody />
      {footer && <div className="footer">{footer?.map((item) => item)}</div>}
    </div>
  );
}
